import { useEffect, useState } from 'react';
import { create } from '@bufbuild/protobuf';
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Button,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ArchiveIcon from '@mui/icons-material/Archive';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import { ShoppingListItemInputSchema } from '../../generated/shopping/v1/shopping_pb';
import type { ShoppingListSummary } from '../../generated/shopping/v1/shopping_pb';
import {
  listShoppingLists,
  getShoppingList,
  createShoppingList,
  ShoppingListSortField,
  ShoppingListSortOrder,
  ShoppingListStatus,
} from '../api/shoppingList';
import { ErrorAlert } from '../components/ErrorAlert';
import { LoadingBackdrop } from '../components/LoadingBackdrop';

export default function LinkedReceiptsPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [lists, setLists] = useState<ShoppingListSummary[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading completed lists...');
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    void loadLists();
  }, [page, pageSize]);

  const loadLists = async () => {
    setLoadingMessage('Loading completed lists...');
    setIsLoading(true);
    setError(null);

    try {
      // Use server-side filtering for paired receipts
      const response = await listShoppingLists({
        pageSize,
        page,
        sortBy: ShoppingListSortField.UPDATED_AT,
        sortOrder: ShoppingListSortOrder.DESC,
        hasPairedReceipt: true,
      });
      setLists(response.shoppingLists);
      setTotalCount(response.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load completed lists.');
    } finally {
      setIsLoading(false);
    }
  };

  const isListComplete = (list: ShoppingListSummary) =>
    list.status === ShoppingListStatus.COMPLETE ||
    (list.itemCount > 0 && list.checkedCount === list.itemCount);

  const handleCopyList = async (listId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setLoadingMessage('Copying list...');
    setIsLoading(true);
    setError(null);

    try {
      // Fetch the full list with items
      const response = await getShoppingList({ listId });
      if (!response.shoppingList) {
        throw new Error('List not found');
      }

      const sourceList = response.shoppingList;

      // Create items for the new list (without checked status)
      const items = sourceList.items.map((item) =>
        create(ShoppingListItemInputSchema, {
          description: item.description,
          customDescription: item.customDescription || undefined,
          quantity: item.quantity ?? 1,
          unitPrice: item.unitPrice,
          price: item.price,
          category: item.category || undefined,
        })
      );

      // Create new list without receipt association
      const createResponse = await createShoppingList({
        name: sourceList.name ? `${sourceList.name} (Copy)` : 'Copied List',
        store: sourceList.store || undefined,
        items,
        // No sourceReceiptId - this creates an unassociated list
      });

      if (createResponse.shoppingList) {
        // Navigate to the shopping lists page to see the new list
        navigate('/lists');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to copy list.');
    } finally {
      setIsLoading(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  return (
    <Box sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, sm: 3, md: 3 }, maxWidth: 1200, mx: 'auto', overflowX: 'hidden' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary', fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
          Completed Lists
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Shopping lists paired with receipts. Copy to reuse as a new list.
        </Typography>
      </Box>

      <ErrorAlert message={error} onDismiss={() => setError(null)} />

      {lists.length === 0 && !isLoading ? (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <CardContent
            sx={{
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            <ArchiveIcon
              sx={{
                fontSize: 64,
                color: 'text.disabled',
                mb: 2,
              }}
            />
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Completed Lists
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Lists appear here when paired with a receipt.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ 
              borderRadius: 2, 
              mb: 2,
              display: { xs: 'none', md: 'block' },
              border: `1px solid ${theme.palette.divider}`,
              overflow: 'hidden'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Store</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>List Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lists.map((list) => (
                  <TableRow
                    key={list.listId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/receipt/${list.pairedReceiptId}`)}
                  >
                    <TableCell>
                      <Typography variant="body2">
                        {list.date || 'No date'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="500">
                        {list.store || 'Unknown store'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {list.name || 'Untitled List'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {isListComplete(list) ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Complete"
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      ) : (
                        <Chip
                          label={`${list.checkedCount}/${list.itemCount} checked`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Copy to new list">
                          <IconButton
                            onClick={(e) => handleCopyList(list.listId, e)}
                            color="primary"
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Receipt">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/receipt/${list.pairedReceiptId}`);
                            }}
                            color="primary"
                            size="small"
                          >
                            <ReceiptIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Shopping List">
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/lists', { state: { viewListId: list.listId, isArchived: true } });
                            }}
                            color="primary"
                            size="small"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Stack spacing={2} sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
            {lists.map((list) => (
              <Card
                key={list.listId}
                elevation={0}
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
                onClick={() => navigate(`/receipt/${list.pairedReceiptId}`)}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ minWidth: 0, mr: 2 }}>
                        <Typography variant="subtitle1" fontWeight="700" noWrap sx={{ lineHeight: 1.2, mb: 0.5 }}>
                          {list.store || 'Unknown Store'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {list.date || 'No Date'}
                        </Typography>
                      </Box>
                      {isListComplete(list) ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                          label="Complete"
                          size="small"
                          color="success"
                          variant="filled"
                        />
                      ) : (
                        <Chip
                          label={`${list.checkedCount}/${list.itemCount}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    <Box>
                      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                        {list.name || 'Untitled List'}
                      </Typography>
                    </Box>

                    <Divider />

                    <Box display="flex" justifyContent="flex-end" alignItems="center">
                      <Stack direction="row" spacing={1.5}>
                        <Tooltip title="Copy to new list">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ContentCopyIcon />}
                            onClick={(e) => handleCopyList(list.listId, e)}
                            sx={{ borderRadius: 2, px: 2 }}
                          >
                            Copy
                          </Button>
                        </Tooltip>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/receipt/${list.pairedReceiptId}`);
                          }}
                          sx={{
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          }}
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/lists', { state: { viewListId: list.listId, isArchived: true } });
                          }}
                          sx={{
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            borderRadius: 1.5,
                            bgcolor: alpha(theme.palette.primary.main, 0.02),
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>

          {/* Pagination Controls */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Per page:
                  </Typography>
                  <ToggleButtonGroup
                    value={pageSize}
                    exclusive
                    onChange={(_, size) => size && handlePageSizeChange(size)}
                    size="small"
                  >
                    <ToggleButton value={10}>10</ToggleButton>
                    <ToggleButton value={25}>25</ToggleButton>
                    <ToggleButton value={50}>50</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack 
                  direction={{ xs: 'column', sm: 'row' }} 
                  spacing={2} 
                  alignItems="center"
                  sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {totalCount > 0
                      ? `${(page - 1) * pageSize + 1}-${Math.min(page * pageSize, totalCount)} of ${totalCount}`
                      : '0 results'}
                  </Typography>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                    siblingCount={isMobile ? 0 : 1}
                    boundaryCount={1}
                    showFirstButton={!isMobile}
                    showLastButton={!isMobile}
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </>
      )}

      <LoadingBackdrop open={isLoading} message={loadingMessage} />
    </Box>
  );
}
